import {
  type AppointmentsSearchResponse,
  type Task,
  fetcher,
  stringifyAppointmentSearchQuery,
  stringifyGetTasksQuery,
} from "@repo/api";
import { addDays, addMinutes, isWithinInterval } from "date-fns";
import useSWR from "swr";

export function useIsCollidingAppointments(
  emails?: string[],
  fromDate?: Date,
  toDate?: Date,
  currentTaskId?: number
) {
  return useSWR<boolean>(
    fromDate
      ? `${emails?.join(",") ?? ""}${fromDate.getTime()}${toDate?.getTime()}`
      : null,
    async () => {
      try {
        const tasks = await fetcher.core.get<Task[]>(
          `/opportunities/tasks?${stringifyGetTasksQuery({
            fromDate,
            emails,
            toDate,
          })}`
        );

        if (tasks.length > 0 && fromDate) {
          const existing = tasks
            .filter((t) => t.id !== currentTaskId)
            .filter((t) => t.isTimeSet)
            .filter((t) => {
              if (!t.taskStartDateTime) {
                return false;
              }
              const from = isWithinInterval(addMinutes(fromDate, 1), {
                start: new Date(t.taskStartDateTime),
                end: t.taskEndDateTime
                  ? new Date(t.taskEndDateTime)
                  : addMinutes(new Date(t.taskStartDateTime), 29),
              });
              const to = toDate
                ? isWithinInterval(addMinutes(toDate, -1), {
                    start: new Date(t.taskStartDateTime),
                    end: t.taskEndDateTime
                      ? new Date(t.taskEndDateTime)
                      : addMinutes(new Date(t.taskStartDateTime), 29),
                  })
                : false;
              return from || to;
            });
          if (existing.length > 0) {
            return true;
          }
        }
      } catch {
        /* Eat exceptions */
      }

      const query = stringifyAppointmentSearchQuery(
        {
          appointmentDate: [fromDate!, toDate ?? addDays(fromDate!, 30)],
          emailId: emails ?? [],
        },
        {
          pageIndex: 0,
          pageSize: 10,
          sortKey: "timeWindowStartTime",
          sortOrder: "desc",
        }
      );

      const appointmentResult =
        await fetcher.core.get<AppointmentsSearchResponse>(
          `/opportunities/appointments?${query}`
        );

      return (
        appointmentResult.content.filter((a) =>
          isWithinInterval(fromDate!, {
            start: new Date(a.timeWindowStartTime),
            end: new Date(a.timeWindowEndTime),
          })
        ).length > 0
      );
    }
  );
}
